import {Component, ElementRef, Inject, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef} from "@angular/material/dialog";
import {UserResponse} from "../interface/userResponse";
import {
  ProfileImageCropperDialogComponent
} from "../profile-image-cropper-dialog/profile-image-cropper-dialog.component";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ARM_PHONE_NUMBER_PATTERN, EMAIL_PATTERN, PHONE_NUMBER_PATTERN,} from "../constants/validation.constants";
import {UserCardUtil} from "../utilityClass/userCardUtil";
import {UserImageResponse} from "../interface/userImageResponse";
import {UserService} from "../service/user.service";
import {UserUpdateRequest} from "../interface/userUpdateRequest";
import {Router} from "@angular/router";
import {CookieService} from "ngx-cookie-service";
import intlTelInput from "intl-tel-input";
import {
  EMAIL_PATTERN_ERROR,
  EMAIL_REQUIRED_ERROR,
  LENGTH_ERROR,
  NAME_REQUIRED_ERROR,
  PHONE_NUMBER_PATTERN_ERROR,
  PHONE_NUMBER_REQUIRED_ERROR,
  SURNAME_REQUIRED_ERROR
} from "../constants/errorMessages";
import {DEFAULT_INITIAL_COUNTRY, DEFAULT_ITI_POLITE} from "../constants/global.constants";
import {HttpErrorResponse} from "@angular/common/http";
import {MockupDataModel} from "../qr-copy-message/mockup-data.model";
import {MockupType} from "../qr-copy-message/mockup-type.enum";
import {SnackBarUtil} from "../qr-copy-message/snack-bar-util.service";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-user-edit-profile',
  templateUrl: './user-edit-profile.component.html',
  styleUrl: './user-edit-profile.component.scss'
})
export class UserEditProfileComponent {

  public readonly ARMENIA_ISO = "374"

  editForm: FormGroup;
  user: UserResponse | any;
  tempProfileImage: File | null = null;
  currentProfileImage: any;
  iti: any;
  currentProfileImageUrl: string | null = null;
  errorMessage: string | null = null;
  isDeleted: boolean = false;
  isPhoneChanged: boolean = false;
  isEmailChanged: boolean = false;
  isSaveClicked: boolean = false;
  emailExists: boolean = false;
  phoneExists: boolean = false;

  @ViewChild('phoneInput') phone?: ElementRef<HTMLInputElement>;

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: any,
    private readonly router: Router,
    protected readonly dialogRef: MatDialogRef<UserEditProfileComponent>,
    private readonly dialog: MatDialog,
    private readonly formBuilder: FormBuilder,
    private readonly cookieService: CookieService,
    private readonly userCardUtil: UserCardUtil,
    private readonly userService: UserService,
    private readonly snackBarUtil: SnackBarUtil,
    private readonly translate: TranslateService
  ) {
    this.user = data.user;
    this.editForm = this.createEditForm();
    this.fetchProfileImage(this.user.id);
    this.checkPhoneChanges();
    this.checkEmailChanges();
 }

  ngAfterViewInit(): void {
    const phoneInput: HTMLInputElement | any = this.phone?.nativeElement;
    if (phoneInput) {
      this.iti = intlTelInput(phoneInput, {
        initialCountry: DEFAULT_INITIAL_COUNTRY,
        autoPlaceholder: DEFAULT_ITI_POLITE,
        separateDialCode: true,
        utilsScript: "node_modules/intl-tel-input/build/js/utils.js"
      } as intlTelInput.Options);
    }
    phoneInput.parentNode.style.display = 'block';
  }

  get countryIso() {
    return this.iti.getSelectedCountryData().dialCode;
  }

  closeDialog(data?: any): void {
    this.dialogRef.close(data);
  }

  getImageAsSource(file: File | null): string {
    return file ? URL.createObjectURL(file) : 'assets/svg/defaultUserIcon.svg';
  }

  deleteProfileImage(): void {
    if (this.tempProfileImage) {
      this.deleteTempProfileImage();
    } else {
      this.deleteCurrentProfileImage();
    }
  }

  private deleteTempProfileImage(): void {
    this.tempProfileImage = null;
    this.isDeleted = true;
    this.currentProfileImage = null;
  }

  private deleteCurrentProfileImage(): void {
    this.currentProfileImageUrl = null;
    this.isDeleted = true;
    this.currentProfileImage = null;
  }

  isDefaultImage(): boolean {
    return !this.tempProfileImage;
  }

  onProfileImageChange(event: any, inputEl: HTMLInputElement): void {
    if (event.target.files.length === 0) return;
    this.isDeleted = false;
    this.currentProfileImageUrl = null;
    const dialogConfig = this.getDialogConfig({imageChangedEvent: event})
    const dialogRef = this.dialog.open(ProfileImageCropperDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const fileName = this.getFileName(event.target.files[0]);
        this.tempProfileImage = new File([result], fileName, {type: result.type});
      }
      inputEl.value = '';
    });
  }

  onCancel(event: Event): void {
    event.preventDefault();
    this.tempProfileImage = null;
    this.currentProfileImageUrl = null;
    this.fetchProfileImage(this.user.id);
    this.closeDialog();
  }

  fetchProfileImage(userId: number): void {
    if (this.user.profilePic !== null) {
      this.userService.getProfileImage(BigInt(userId)).subscribe((imageData) => {
        this.handleProfileImageData(imageData);
      });
    }
  }

  private handleProfileImageData(imageData: Blob): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      this.currentProfileImage = e.target?.result as string;
      this.currentProfileImageUrl = this.currentProfileImage;
      this.userService.setCurrentProfileImageUrl(this.currentProfileImageUrl);
    };
    reader.readAsDataURL(imageData);
  }

  private getDialogConfig(data: any): MatDialogConfig {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = data;
    dialogConfig.maxWidth = '700px';
    dialogConfig.width = 'auto';
    return dialogConfig;
  }

  getFileName(file: File): string {
    return file.name;
  }

  createEditForm(): FormGroup {
    return this.formBuilder.group({
      name: [this.user ? this.user.name : '', [Validators.required, Validators.minLength(2), Validators.maxLength(50), this.userCardUtil.whitespaceValidator()]],
      surname: [this.user ? this.user.surname : '', [Validators.required, Validators.minLength(2), Validators.maxLength(50), this.userCardUtil.whitespaceValidator()]],
      email: [this.user ? this.user.email : '', [Validators.required, Validators.pattern(EMAIL_PATTERN)]],
      phoneNumber: [this.user ? this.user.phoneNumber : '', [Validators.required, Validators.pattern(PHONE_NUMBER_PATTERN)]]
    })
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.editForm.get(controlName);

    if (control?.invalid && (control?.dirty || control?.touched)) {
      switch (controlName) {
        case 'name':
          if (control.errors?.['required']) {
            return this.translate.instant('form_errors.name_required');
          } else if (control.errors?.['minlength'] || control.errors?.['maxlength']) {
            return this.translate.instant('form_errors.length_error');
          }
          break;

        case 'surname':
          if (control.errors?.['required']) {
            return this.translate.instant('form_errors.surname_required');
          } else if (control.errors?.['minlength'] || control.errors?.['maxlength']) {
            return this.translate.instant('form_errors.length_error');
          }
          break;

        case 'email':
          if (control.errors?.['required']) {
            return this.translate.instant('form_errors.email_required');
          } else if (control.errors?.['pattern']) {
            return this.translate.instant('form_errors.email_pattern');
          }
          break;

        case 'phoneNumber':
          if (control.errors?.['required']) {
            return this.translate.instant('form_errors.phone_required');
          } else if (control.errors?.['pattern'] || control.errors?.['invalidPhone']) {
            return this.translate.instant('form_errors.phone_pattern');
          }
          break;

        default:
          return null;
      }
    }

    return null;
  }

  isControlInvalid(controlName: string) {
    const control = this.editForm.get(controlName);
    return control?.invalid && control?.touched;
  }

  edit() {
    this.editForm.markAllAsTouched();

    const cleanedPhoneNumber = this.cleanPhoneNumber();
    this.updatePhoneNumberInForm(cleanedPhoneNumber);

    const editUser: UserUpdateRequest = this.createUserUpdateRequest(cleanedPhoneNumber);

    if (this.editForm.valid) {
      this.isSaveClicked = true;
      if (this.isDeleted || this.tempProfileImage != null) {
        this.uploadProfileImageAndSaveUser(editUser);
      } else {
        this.saveUser(editUser);
      }
    }
  }

  isSaveButtonDisabled(): boolean {
    const phoneControl = this.editForm.get('phoneNumber');
    const isPhoneValid = phoneControl?.valid && (!this.iti || this.iti.isValidNumber());

    return this.editForm.invalid ||
           this.isSaveClicked ||
           !isPhoneValid ||
           (this.isEmailChanged && this.emailExists) ||
           (this.isPhoneChanged && this.phoneExists);
  }

  private cleanPhoneNumber(): string {
    const countryData = this.iti.getSelectedCountryData();
    const countryISO = countryData.dialCode;
    let tempPhoneNumber = this.editForm.value.phoneNumber.replace(/\D+/g, '');

    const dialCodePattern = new RegExp(`^${countryISO}`);
    return tempPhoneNumber.replace(dialCodePattern, '');
  }

  private updatePhoneNumberInForm(cleanedPhoneNumber: string): void {
    this.editForm.setValue({
      ...this.editForm.value,
      phoneNumber: cleanedPhoneNumber
    });
  }

  private createUserUpdateRequest(cleanedPhoneNumber: string): UserUpdateRequest {
    return {
      name: this.editForm.value.name,
      surname: this.editForm.value.surname,
      email: this.editForm.value.email,
      phoneNumber: cleanedPhoneNumber,
      countryIso: this.countryIso
    };
  }

  private uploadProfileImageAndSaveUser(editUser: UserUpdateRequest): void {
    const formData = new FormData();
    formData.append('file', this.tempProfileImage ? this.tempProfileImage : '');

    this.userService.uploadUserImage(this.user.id, formData).subscribe({
      next: (responseImage: UserImageResponse) => {
        this.user.profilePic = responseImage.profileImage;
        this.userService.setUser(this.user);
        this.saveUser(editUser);
      },
      error: error => {
        this.isSaveClicked = false;
        console.error('Image upload failed', error);
      }
    });
  }

  private saveUser(editUser: UserUpdateRequest): void {
    this.userService.editUser(this.user.id, editUser).subscribe({
      next: this.handleUserUpdateSuccess.bind(this),
      error: (error: HttpErrorResponse) => {
        this.handleUserUpdateError(error, editUser)
      }
    });
  }

  private handleUserUpdateSuccess(response: any): void {
    this.userService.setUser(response);
    const translatedMessage = this.translate.instant('success_message');
    const mockupData = new MockupDataModel(translatedMessage, MockupType.SUCCESS);
    this.showSnackbar(mockupData, SnackBarUtil.CLASSES.success);
    setTimeout(() => {
      const data = {
        user: this.editedUser,
        isPictureChanged: true,
        isCurrentProfilePictureDeleted: this.isDeleted && this.currentProfileImage == null
      }
      this.closeDialog(data);
    }, 3000);
  }

  private handleUserUpdateError(error: HttpErrorResponse, editUser: UserUpdateRequest): void {
    const message = error.error.message;
    if (message.startsWith("User with email")) {
      this.isSaveClicked = false;
      this.translate.get('email_already_linked_error').subscribe({
        next: (translatedText: string) => {
          const mockupData = new MockupDataModel(translatedText, MockupType.ERROR);
          this.showSnackbar(mockupData, SnackBarUtil.CLASSES.error);
        }
      });
      return;
    }

    if (message == "Phone number already exists") {
      this.isSaveClicked = false;
      this.translate.get('phone_already_linked_error').subscribe({
        next: (translatedText: string) => {
          const mockupData = new MockupDataModel(translatedText, MockupType.ERROR);
          this.showSnackbar(mockupData, SnackBarUtil.CLASSES.error);
        }
      });
      return;
    }

    if (error.status === 409) {
      const mockupData = new MockupDataModel(this.translate.instant('profile_successfully_updated'), MockupType.SUCCESS);
      this.showSnackbar(mockupData, SnackBarUtil.CLASSES.success);
      setTimeout(() => {
        const data = {
          user: this.editedUser
        }
        this.closeDialog(data);
        this.router.navigate(['/verify-phone'], {
          state: {
            userPhoneNumber: editUser.phoneNumber,
            countryISO: editUser.countryIso
          }
        });
      }, 3000);
    } else {
      this.isSaveClicked = false;
      console.error('User update failed', error);
    }
  }

  private checkPhoneChanges() {
    const formPhoneNumber = this.editForm.get('phoneNumber');

    formPhoneNumber?.valueChanges.subscribe({
      next: (changedNumber) => {
        this.setPhoneNumberValidatorsByCountryISO(this.countryIso);
        const preparedChangedPhone = this.countryIso + changedNumber.replaceAll(' ', '');
        const preparedUserPhone = this.user.phoneNumber.replaceAll('+', '').replaceAll(' ', '');
        this.isPhoneChanged = !(preparedChangedPhone == preparedUserPhone);
        if (this.iti) {
          const isValid = this.iti.isValidNumber();
          if (!isValid) {
            formPhoneNumber.setErrors({ 'invalidPhone': true });
          } else {
            formPhoneNumber.setErrors(null);
          }
        }
      }
    });
  }

  private setPhoneNumberValidatorsByCountryISO(countryISO: string) {
    const formPhoneNumber = this.editForm.get('phoneNumber');

    if (countryISO == this.ARMENIA_ISO) {
      formPhoneNumber?.setValidators([Validators.required, Validators.pattern(ARM_PHONE_NUMBER_PATTERN)]);
    } else {
      formPhoneNumber?.setValidators([Validators.required, Validators.pattern(PHONE_NUMBER_PATTERN)]);
    }
    formPhoneNumber?.updateValueAndValidity();
  }

  private checkEmailChanges() {
    const emailControl = this.editForm.get('email');
    emailControl?.valueChanges.subscribe({
      next: (changedEmail) => {
        this.isEmailChanged = !(changedEmail == this.user.email);
      }
    })
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBarUtil.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data,
    });
  }

  get editedUser() {
    const controls = this.editForm.controls;
    const phoneNumberWithIso = `+${this.countryIso}${controls['phoneNumber'].value}`;
    const builtUser: UserResponse = {
      id: this.user.id,
      name: controls['name'].value,
      surname: controls['surname'].value,
      email: controls['email'].value,
      phoneNumber: phoneNumberWithIso,
      countryIso: this.countryIso,
      profilePic: this.user.profilePic,
      resetPasswordToken: this.user.resetPasswordToken,
      active: this.user.active,
      showDetail: false
    }
    return builtUser;
  }
}
